/**
 * Master JSON Parser: Compiles streamlined object-centric JSON scripts into
 * executable ASAE Scenes, Characters, SVG Objects, Lifecycles, Timelines, Audio, and Quizzes.
 */
import { ASAEScript, SceneSchema, ObjectSchema, ObjectActionSchema, ObjectAnimationTrackSchema } from './Schema';
import { Validation } from './Validation';
import { Scene } from '../core/Scene';
import { Character } from '../characters/Character';
import { Shape } from '../objects/Shape';
import { TextObject } from '../objects/Text';
import { Arrow } from '../objects/Arrow';
import { PathObject } from '../objects/Path';
import { Bubble } from '../objects/Bubble';
import { Label } from '../objects/Label';
import { Chalkboard } from '../objects/Chalkboard';
import { Compass } from '../objects/Compass';
import { Ruler } from '../objects/Ruler';
import { TableObject } from '../objects/Table';
import { FlowNode } from '../objects/Flowchart';
import { ImageObject } from '../objects/Image';
import { StoryEngine } from '../story/StoryEngine';
import { GroupObject } from '../objects/Group';
import { FireEffect } from '../effects/Fire';
import { RainEffect } from '../effects/Rain';
import { SparklesEffect } from '../effects/Sparkles';
import { ConfettiEffect } from '../effects/Confetti';
import { GeometryObject } from '../objects/GeometryObject';
import { RawSVGObject } from '../objects/RawSVGObject';
import { GeometryEngine } from '../geometry/core/GeometryEngine';
import { QuizObject } from '../interaction/Quiz';
import { AudioManager } from '../audio/AudioManager';
import { VoicePlayer } from '../audio/VoicePlayer';
import { PointerObject } from '../objects/PointerObject';
import { SpotlightObject } from '../objects/SpotlightObject';
import { SubtitleOverlay } from '../audio/Subtitle';
import { NarrationCoordinator } from '../audio/Narration';

export class JSONParser {
  private static voicePlayer: VoicePlayer = new VoicePlayer();

  public static getVoicePlayer(): VoicePlayer {
    return this.voicePlayer;
  }

  public static normalizeScript(script: any): ASAEScript {
    if (!script || typeof script !== 'object') return script;

    if (!script.config && script.settings && typeof script.settings === 'object') {
      script.config = script.settings;
    }
    if (!script.config) {
      script.config = {};
    }

    // If script has no scenes array or it is empty, provide a default scene structure to prevent validation errors
    if (!script.scenes || !Array.isArray(script.scenes) || script.scenes.length === 0) {
      const pageTitle = script.title || script.name || 'New Scene';
      script.scenes = [
        {
          id: 'scene-1',
          duration: 25,
          background: '#0f172a',
          objects: [
            {
              id: 'text-title',
              type: 'text',
              text: pageTitle,
              x: 960,
              y: 540,
              style: {
                fill: '#ffffff',
                fontSize: 60,
                fontFamily: 'Inter',
                textAnchor: 'middle',
                dominantBaseline: 'central',
                fontWeight: 'bold'
              }
            }
          ]
        }
      ];
    }

    if (Array.isArray(script.scenes)) {
      script.scenes.forEach((scene: any, sIdx: number) => {
        if (!scene.id) scene.id = `scene-${sIdx + 1}`;
        if (!scene.duration || typeof scene.duration !== 'number') scene.duration = 25;

        // Story Animation Scene Compilation (type: "story")
        if (StoryEngine.isStoryScene(scene)) {
          StoryEngine.compileStoryScene(scene);
        }

        // Background object normalization (e.g. { type: "color", value: "#0f172a" })
        if (scene.background && typeof scene.background === 'object') {
          if (scene.background.value) {
            scene.background = scene.background.value;
          } else if (scene.background.color) {
            scene.background = scene.background.color;
          } else {
            scene.background = '#0f172a';
          }
        }

        // Objects normalization: Convert object map {} into array [] if needed
        if (scene.objects && !Array.isArray(scene.objects) && typeof scene.objects === 'object') {
          const objsArray: any[] = [];
          Object.keys(scene.objects).forEach(key => {
            const item = scene.objects[key];
            if (item && typeof item === 'object') {
              if (!item.id) item.id = key;
              objsArray.push(item);
            }
          });
          scene.objects = objsArray;
        }

        const isTopLeftCoords = (
          scene.coordinateOrigin === 'topLeft' ||
          (scene as any).coordinateMode === 'screen' ||
          (typeof scene.id === 'string' && (
            scene.id.includes('word') || 
            scene.id.includes('winapp') || 
            scene.id.includes('screencast') || 
            scene.id.includes('live') ||
            scene.id.startsWith('scene-live')
          ))
        ) && scene.coordinateOrigin !== 'centered';

        if (Array.isArray(scene.objects)) {
          scene.objects.forEach((obj: any, oIdx: number) => {
            if (!obj.id) obj.id = `obj-${sIdx}-${oIdx}`;
            
            // Normalize position object { x: 960, y: 70 } -> x: 960, y: 70
            if (obj.position && typeof obj.position === 'object') {
              if (obj.x === undefined) obj.x = obj.position.x ?? 0;
              if (obj.y === undefined) obj.y = obj.position.y ?? 0;
            }

            // Automatic coordinate conversion from top-left (0..1920, 0..1080) to centered (-960..+960, -540..+540)
            if (isTopLeftCoords) {
              if (obj.type === 'image') {
                // ImageObject in ASAE is centered around (x, y) with viewBox -960 -540 1920 1080.
                // A top-left image at (x, y) with (width, height) has center at (x + width/2 - 960, y + height/2 - 540).
                const w = obj.props?.width || 1920;
                const h = obj.props?.height || 1080;
                obj.x = (typeof obj.x === 'number' ? obj.x : 0) + w / 2 - 960;
                obj.y = (typeof obj.y === 'number' ? obj.y : 0) + h / 2 - 540;
              } else {
                if (typeof obj.x === 'number') obj.x -= 960;
                if (typeof obj.y === 'number') obj.y -= 540;
              }

              if (Array.isArray(obj.animations)) {
                obj.animations.forEach((anim: any) => {
                  if (anim.property === 'x' && Array.isArray(anim.keyframes)) {
                    anim.keyframes.forEach((kf: any) => {
                      if (typeof kf.value === 'number') kf.value -= 960;
                    });
                  } else if (anim.property === 'y' && Array.isArray(anim.keyframes)) {
                    anim.keyframes.forEach((kf: any) => {
                      if (typeof kf.value === 'number') kf.value -= 540;
                    });
                  }
                });
              }

              if (obj.props) {
                if (typeof obj.props.targetX === 'number') obj.props.targetX -= 960;
                if (typeof obj.props.targetY === 'number') obj.props.targetY -= 540;
              }
            }
          });

          if (isTopLeftCoords) {
            scene.coordinateOrigin = 'centered';
          }
        }

        // Narration array normalization into audio array
        if (Array.isArray(scene.narration)) {
          if (!Array.isArray(scene.audio)) scene.audio = [];
          scene.narration.forEach((n: any, nIdx: number) => {
            scene.audio.push({
              id: `narration-${nIdx}`,
              type: 'speech',
              time: n.startTime ?? n.time ?? 0,
              speakerId: n.speaker || n.speakerId || 'teacher',
              text: n.text,
              subtitles: true
            });
          });
        }
      });
    }

    return script;
  }

  public static parse(script: ASAEScript, audioManager: AudioManager): Scene[] {
    const normalizedScript = JSONParser.normalizeScript(script);

    const scriptLang = (normalizedScript as any).language || (normalizedScript as any).lang || normalizedScript.config?.language || normalizedScript.config?.lang;
    if (scriptLang) {
      JSONParser.voicePlayer.setDefaultLanguage(scriptLang);
    }

    const errors = Validation.validate(normalizedScript);
    if (errors.some(e => e.severity === 'error')) {
      console.error('JSON Schema Validation Errors:', errors);
      const firstError = errors.find(e => e.severity === 'error') || errors[0];
      throw new Error(`Invalid ASAE JSON: ${firstError.message} at ${firstError.path}`);
    }

    const scenes: Scene[] = [];

    for (const sceneSchema of normalizedScript.scenes) {
      const scene = JSONParser.parseScene(sceneSchema, normalizedScript, audioManager);
      scenes.push(scene);
    }

    return scenes;
  }

  public static parseScene(sSchema: SceneSchema, rootScript: ASAEScript, audioManager: AudioManager): Scene {
    const scene = new Scene({
      id: sSchema.id,
      title: sSchema.title,
      topic: sSchema.topic,
      duration: sSchema.duration,
      background: sSchema.background || rootScript.config?.background || '#0f172a'
    });

    // Subtitle overlay for voiceovers
    const subtitleOverlay = new SubtitleOverlay({
      id: `subtitle-${sSchema.id}`,
      x: 0,
      y: 0,
      visible: false
    });
    scene.addObject(subtitleOverlay);

    const narrationCoordinator = new NarrationCoordinator(JSONParser.voicePlayer, audioManager);
    narrationCoordinator.setSceneContext(scene, subtitleOverlay);

    // 1. Instantiate Objects & Apply Lifecycles (appearTime, disappearTime, enter, exit, actions, animations)
    if (sSchema.objects) {
      for (const oSchema of sSchema.objects) {
        const obj = JSONParser.parseObject(oSchema, rootScript);
        if (!obj) continue;

        scene.addObject(obj);

        (obj as any).appearTime = oSchema.appearTime;
        (obj as any).disappearTime = oSchema.disappearTime;
        (obj as any).enter = oSchema.enter;
        (obj as any).exit = oSchema.exit;
        (obj as any).actions = oSchema.actions;
        (obj as any).animations = oSchema.animations;

        const targetOpacity = oSchema.opacity ?? 1;
        const targetX = oSchema.x ?? 0;
        const targetY = oSchema.y ?? 0;
        const targetScaleX = oSchema.scaleX ?? 1;
        const targetScaleY = oSchema.scaleY ?? 1;

        (obj as any).targetX = targetX;
        (obj as any).targetY = targetY;

        const appear = oSchema.appearTime ?? 0;
        let disappear = oSchema.disappearTime ?? sSchema.duration;

        // Lifecycle Rule: Background, screencast, and canvas images must remain visible for the entire scene duration
        // unless an explicit exit animation is specified.
        if (oSchema.type === 'image' && (!oSchema.exit || oSchema.exit.type === 'none')) {
          disappear = Math.max(disappear, sSchema.duration);
        }

        // Lifecycle Rule A: If object appears after t=0, hide it initially
        if (appear > 0) {
          obj.visible = false;
          obj.opacity = 0;

          // Action to keep it hidden before appearTime
          scene.timeline.addAction({
            id: `lifecycle-pre-${obj.id}`,
            name: 'pre-appear-hide',
            startTime: 0,
            duration: appear,
            targetId: obj.id,
            execute: () => {
              obj.visible = false;
              obj.opacity = 0;
            }
          });
        }

        // Lifecycle Rule B: Enter Transition at appearTime
        const enterTrans = oSchema.enter;
        const enterDur = enterTrans?.duration ?? (enterTrans?.type && enterTrans.type !== 'none' ? 0.6 : 0.01);

        scene.timeline.addAction({
          id: `lifecycle-enter-${obj.id}`,
          name: 'enter-transition',
          startTime: appear,
          duration: enterDur,
          targetId: obj.id,
          onStart: () => {
            obj.visible = true;
          },
          execute: (progress) => {
            const curTargetX = (obj as any).targetX ?? targetX;
            const curTargetY = (obj as any).targetY ?? targetY;
            obj.visible = true;
            const hasXAnim = oSchema.animations?.some(a => a.property === 'x');
            const hasYAnim = oSchema.animations?.some(a => a.property === 'y');
            if (!enterTrans || enterTrans.type === 'none') {
              obj.opacity = targetOpacity;
              if (!hasXAnim) obj.x = curTargetX;
              if (!hasYAnim) obj.y = curTargetY;
              obj.scaleX = targetScaleX;
              obj.scaleY = targetScaleY;
              if (typeof (obj as any).applyTransform === 'function') {
                (obj as any).applyTransform();
              }
            } else if (enterTrans.type === 'fadeIn') {
              obj.opacity = targetOpacity * progress;
            } else if (enterTrans.type === 'popIn' || enterTrans.type === 'zoomIn') {
              obj.opacity = targetOpacity * Math.min(1, progress * 1.5);
              const bounceScale = Math.sin(progress * Math.PI * 0.5);
              obj.scaleX = targetScaleX * bounceScale;
              obj.scaleY = targetScaleY * bounceScale;
            } else if (enterTrans.type === 'slideIn') {
              obj.opacity = targetOpacity * Math.min(1, progress * 2);
              const fromDir = enterTrans.from || 'bottom';
              const dist = enterTrans.distance || 300;
              if (fromDir === 'bottom') obj.y = curTargetY + dist * (1 - progress);
              else if (fromDir === 'top') obj.y = curTargetY - dist * (1 - progress);
              else if (fromDir === 'left') obj.x = curTargetX - dist * (1 - progress);
              else if (fromDir === 'right') obj.x = curTargetX + dist * (1 - progress);
            }
          },
          onComplete: () => {
            const curTargetX = (obj as any).targetX ?? targetX;
            const curTargetY = (obj as any).targetY ?? targetY;
            obj.visible = true;
            obj.opacity = targetOpacity;
            const hasXAnim = oSchema.animations?.some(a => a.property === 'x');
            const hasYAnim = oSchema.animations?.some(a => a.property === 'y');
            if (!hasXAnim) obj.x = curTargetX;
            if (!hasYAnim) obj.y = curTargetY;
            obj.scaleX = targetScaleX;
            obj.scaleY = targetScaleY;
            if (typeof (obj as any).applyTransform === 'function') {
              (obj as any).applyTransform();
            }
          }
        });

        // Lifecycle Rule C: Exit Transition and disappearTime
        if (disappear < sSchema.duration) {
          const exitTrans = oSchema.exit;
          const exitDur = exitTrans?.duration ?? (exitTrans?.type && exitTrans.type !== 'none' ? 0.6 : 0.01);
          const exitStart = Math.max(appear, disappear - exitDur);

          if (exitTrans && exitTrans.type !== 'none') {
            scene.timeline.addAction({
              id: `lifecycle-exit-${obj.id}`,
              name: 'exit-transition',
              startTime: exitStart,
              duration: exitDur,
              targetId: obj.id,
              execute: (progress) => {
                const curTargetX = (obj as any).targetX ?? targetX;
                const curTargetY = (obj as any).targetY ?? targetY;
                const inv = 1 - progress;
                if (exitTrans.type === 'fadeOut') {
                  obj.opacity = targetOpacity * inv;
                } else if (exitTrans.type === 'popOut' || exitTrans.type === 'zoomOut') {
                  obj.opacity = targetOpacity * inv;
                  obj.scaleX = targetScaleX * inv;
                  obj.scaleY = targetScaleY * inv;
                } else if (exitTrans.type === 'slideOut') {
                  obj.opacity = targetOpacity * inv;
                  const toDir = exitTrans.to || 'bottom';
                  const dist = exitTrans.distance || 300;
                  if (toDir === 'bottom') obj.y = curTargetY + dist * progress;
                  else if (toDir === 'top') obj.y = curTargetY - dist * progress;
                  else if (toDir === 'left') obj.x = curTargetX - dist * progress;
                  else if (toDir === 'right') obj.x = curTargetX + dist * progress;
                }
              }
            });
          }

          // Action to hide completely after disappearTime
          scene.timeline.addAction({
            id: `lifecycle-post-${obj.id}`,
            name: 'post-disappear-hide',
            startTime: disappear,
            duration: Math.max(0.1, sSchema.duration - disappear),
            targetId: obj.id,
            execute: () => {
              obj.visible = false;
              obj.opacity = 0;
            }
          });
        }

        // 2. Parse Object-Embedded Keyframe Animations
        if (oSchema.animations) {
          for (const anim of oSchema.animations) {
            scene.timeline.addTrack({
              id: `track-${obj.id}-${anim.property}`,
              targetId: obj.id,
              property: anim.property,
              enabled: true,
              keyframes: anim.keyframes.map(k => ({
                time: k.time,
                value: k.value,
                easing: k.easing || 'easeInOutCubic'
              })),
              onUpdate: (val, targetId, prop) => {
                if (prop.startsWith('props.')) {
                  const subProp = prop.replace('props.', '');
                  if ((obj as any).props) {
                    (obj as any).props[subProp] = val;
                    obj.render();
                  }
                } else {
                  (obj as any)[prop] = val;
                  if (typeof (obj as any).applyTransform === 'function') {
                    (obj as any).applyTransform();
                  }
                }
              }
            });
          }
        }

        // 3. Parse Object-Embedded Semantic Actions
        if (oSchema.actions) {
          for (const act of oSchema.actions) {
            const actionName = act.action || act.type || 'idle';
            const actDur = act.duration || 2.0;

            scene.timeline.addAction({
              id: act.id || `act-${obj.id}-${Math.random().toString().slice(2, 7)}`,
              name: actionName,
              startTime: act.time,
              duration: actDur,
              targetId: obj.id,
              onStart: () => {
                if (obj instanceof Character) {
                  if (act.emotion) obj.setEmotion(act.emotion as any);
                  if (actionName === 'walk') obj.setAction('walking');
                  else if (actionName === 'wave') obj.setAction('waving');
                  else if (actionName === 'write') obj.setAction('writing');
                  else if (actionName === 'point') obj.setAction('pointing');
                  else if (actionName === 'talk') obj.setAction('talking');
                  else if (actionName === 'clap') obj.setAction('clapping');
                } else if (obj instanceof RawSVGObject && actionName === 'highlightPart') {
                  obj.highlightPart(act.params?.partId || 'eyes', actDur, act.params?.color);
                } else if (obj instanceof PointerObject) {
                  if (actionName === 'click' || actionName === 'doubleClick' || actionName === 'rightClick') {
                    const label = act.params?.label || act.params?.actionLabel || (actionName === 'doubleClick' ? '🖱️ Double Click' : '🖱️ Click');
                    obj.triggerClick(act.params?.rippleColor || (actionName === 'rightClick' ? '#f59e0b' : '#38bdf8'), label);
                  } else if (actionName === 'typeText' || actionName === 'showBadge') {
                    if (act.params?.actionLabel || act.params?.label) {
                      obj.showActionBadge(act.params.actionLabel || act.params.label);
                    }
                  }
                  if (act.params?.cursorType) {
                    obj.setCursorType(act.params.cursorType);
                  }
                } else if (obj instanceof SpotlightObject) {
                  if (actionName === 'highlight' || actionName === 'spotlight') {
                    if (act.toX !== undefined && act.toY !== undefined) {
                      obj.setTarget(
                        act.toX,
                        act.toY,
                        act.params?.width ?? obj.props.targetWidth,
                        act.params?.height ?? obj.props.targetHeight
                      );
                    }
                  }
                }
              },
              execute: (progress, absTime, delta) => {
                if (obj instanceof Character) {
                  if (actionName === 'walk' && act.toX !== undefined) {
                    const startX = targetX;
                    obj.x = startX + (act.toX - startX) * progress;
                    if (act.toY !== undefined) {
                      const startY = targetY;
                      obj.y = startY + (act.toY - startY) * progress;
                    }
                  }
                } else if (obj instanceof PointerObject) {
                  if (act.toX !== undefined) {
                    const startX = targetX;
                    obj.x = startX + (act.toX - startX) * progress;
                    if (act.toY !== undefined) {
                      const startY = targetY;
                      obj.y = startY + (act.toY - startY) * progress;
                    }
                    obj.applyTransform();
                  }
                } else if (obj instanceof TextObject && actionName === 'write') {
                  obj.setTypewriter(progress);
                }
              },
              onComplete: () => {
                if (obj instanceof Character) {
                  obj.setAction('idle');
                }
              }
            });
          }
        }
      }
    }

    // 4. Parse Voiceover Audio Tracks & Sound Effects
    let audioList = sSchema.audio ? [...sSchema.audio] : [];

    // Auto-extract step-by-step audio narrations using GeometryEngine's exact mathematical stepStartTimes
    const geoObj = (sSchema.objects || []).find(o => o.type === 'geometry');
    if (geoObj && geoObj.props?.teacherJSON) {
      try {
        const tempGeo = new GeometryObject(geoObj as any);
        tempGeo.render();
        const geomEngine = (tempGeo as any).geomEngine as GeometryEngine | null;

        if (geomEngine) {
          const steps = geomEngine.timeline.getSteps();
          const startTimes = geomEngine.timeline.getStepStartTimes();
          const appearTime = geoObj.appearTime ?? 0.5;

          const hasRecordedAudio = audioList.some(a => (a.src || (a as any).audioUrl));
          const speechItemsInAudio = audioList.filter(a => a.type === 'speech');

          if (!hasRecordedAudio && speechItemsInAudio.length <= 1) {
            const nonSpeech = audioList.filter(a => a.type !== 'speech');
            audioList = [...nonSpeech];

            steps.forEach((step: any, idx: number) => {
              const text = step.narration || step.title;
              if (text) {
                const startSec = appearTime + ((startTimes[idx] || 0) / 1000.0);
                audioList.push({
                  id: `geo-speech-${geoObj.id}-${idx}`,
                  type: 'speech',
                  time: Math.round(startSec * 100) / 100,
                  text: text,
                  speakerId: 'teacher',
                });
              }
            });

            // Adjust scene duration so drawing and narration finish completely
            const totalMs = geomEngine.timeline.getTotalDuration();
            const reqSceneDur = Math.ceil(appearTime + (totalMs / 1000.0) + 1.0);
            if (scene.duration < reqSceneDur) {
              scene.duration = reqSceneDur;
            }
          }
          tempGeo.destroy();
        }
      } catch (err) {
        console.warn('[JSONParser] Geometry audio sync error:', err);
      }
    }

    if (audioList.length > 0) {
      for (const aItem of audioList) {
        const wordCount = aItem.text ? aItem.text.split(/\s+/).filter(Boolean).length : 0;
        const speechDuration = aItem.text ? Math.max(3.0, Math.ceil((wordCount / 2.0) + 1.5)) : 2.0;
        const estDuration = aItem.duration ?? speechDuration;
        const reqSceneDur = Math.ceil((aItem.time ?? aItem.startTime ?? 0) + estDuration + 1.0);

        // Auto-expand scene duration so next scene never cuts off voiceover before completion
        if (scene.duration < reqSceneDur) {
          scene.duration = reqSceneDur;
        }

        scene.timeline.addAction({
          id: `audio-${aItem.id}`,
          name: aItem.type === 'speech' ? 'voiceover' : (aItem.type === 'audio' ? 'voiceover_file' : 'sfx'),
          startTime: aItem.time ?? aItem.startTime ?? 0,
          duration: estDuration,
          onStart: () => {
            if (aItem.type === 'speech' && (aItem.text || aItem.src || (aItem as any).audioUrl)) {
              narrationCoordinator.playNarration({
                id: aItem.id,
                time: aItem.time,
                duration: estDuration,
                text: aItem.text || '',
                speakerId: aItem.speakerId,
                emotion: aItem.emotion,
                soundCue: aItem.soundName as any,
                rate: aItem.rate || rootScript.config?.voiceRate || 1.0,
                pitch: aItem.pitch || rootScript.config?.voicePitch || 1.0,
                language: (aItem as any).language || (aItem as any).lang || (sSchema as any).language || (sSchema as any).lang || rootScript.config?.language || rootScript.config?.lang || (rootScript as any).language || (rootScript as any).lang,
                showSubtitles: aItem.subtitles !== false,
                src: aItem.src || (aItem as any).audioUrl,
                audioUrl: aItem.src || (aItem as any).audioUrl
              });

              // Dynamic Speech-Driven Auto-Highlighting for any subject SVG diagram parts
              const lowerText = (aItem.text || '').toLowerCase();
              const svgObjs = scene.getAllObjects().filter((o: any) => o instanceof RawSVGObject) as RawSVGObject[];
              
              if (svgObjs.length > 0) {
                // Hardcoded fallback mappings for standard EVS parts
                let targetPart: string | null = null;
                let highlightColor = '#fde047';

                if (lowerText.includes('mouth')) { targetPart = 'mouth'; highlightColor = '#ec4899'; }
                else if (lowerText.includes('eye')) { targetPart = 'eyes'; highlightColor = '#f43f5e'; }
                else if (lowerText.includes('ear')) { targetPart = 'ears'; highlightColor = '#a855f7'; }
                else if (lowerText.includes('nose')) { targetPart = 'nose'; highlightColor = '#d97706'; }
                else if (lowerText.includes('teeth') || lowerText.includes('tooth')) { targetPart = 'teeth'; highlightColor = '#eab308'; }
                else if (lowerText.includes('tongue')) { targetPart = 'tongue'; highlightColor = '#ef4444'; }
                else if (lowerText.includes('hand') || lowerText.includes('arm')) { targetPart = 'hands'; highlightColor = '#0284c7'; }
                else if (lowerText.includes('leg') || lowerText.includes('foot')) { targetPart = 'legs'; highlightColor = '#15803d'; }
                else if (lowerText.includes('skin')) { targetPart = 'skin'; highlightColor = '#38bdf8'; }

                // Dynamic matching for custom subject diagram parts (e.g. leaf, root, triangle, cloud, beaker, atom)
                if (!targetPart) {
                  for (const svgObj of svgObjs) {
                    const container = svgObj.getContainer();
                    if (container) {
                      const partEls = container.querySelectorAll('[id^="part-"], [id^="label-"]');
                      for (let i = 0; i < partEls.length; i++) {
                        const rawId = (partEls[i].id || '').toLowerCase().replace(/^(part|label)-/, '');
                        if (rawId.length >= 3 && lowerText.includes(rawId)) {
                          targetPart = rawId;
                          highlightColor = '#38bdf8';
                          break;
                        }
                      }
                    }
                    if (targetPart) break;
                  }
                }

                if (targetPart) {
                  const partToHighlight = targetPart;
                  svgObjs.forEach(svg => svg.highlightPart(partToHighlight, estDuration, highlightColor));
                }
              }
            } else if (aItem.type === 'sfx') {
              audioManager.playSoundCue((aItem.soundName as any) || 'chime');
            } else if (aItem.type === 'audio' && aItem.src) {
              audioManager.playAudioTrack(aItem.src);
            }
          },
          execute: () => {}
        });
      }
    }

    // 7. Parse Interactive Quizzes
    const quizzes = Array.isArray(sSchema.quiz) ? sSchema.quiz : (sSchema.quiz ? [sSchema.quiz] : []);
    for (const q of quizzes) {
      const quizObj = new QuizObject(
        { id: q.id, x: 0, y: 0, visible: false, opacity: 0 },
        q,
        audioManager
      );
      scene.addObject(quizObj);

      const qTime = q.time;
      const qDur = Math.max(1, sSchema.duration - qTime);

      scene.timeline.addAction({
        id: `action-${q.id}`,
        name: 'quiz-trigger',
        startTime: qTime,
        duration: qDur,
        targetId: q.id,
        execute: (progress) => {
          quizObj.visible = true;
          quizObj.opacity = Math.min(1, progress * 4);
        }
      });
    }

    return scene;
  }

  public static createObjectFromConfig(oSchema: any, audioManager?: AudioManager): any {
    return JSONParser.parseObject(oSchema, {} as any);
  }

  public static parseObject(oSchema: any, rootScript?: ASAEScript): any {
    const baseConfig = {
      id: oSchema.id,
      layer: oSchema.layer,
      x: oSchema.x ?? 0,
      y: oSchema.y ?? 0,
      scaleX: oSchema.scaleX ?? 1,
      scaleY: oSchema.scaleY ?? 1,
      rotation: oSchema.rotation ?? 0,
      opacity: oSchema.opacity ?? 1,
      visible: oSchema.visible ?? true,
      filter: oSchema.filter,
      isInteractive: oSchema.isInteractive ?? true,
      appearTime: oSchema.appearTime ?? 0,
      disappearTime: oSchema.disappearTime ?? Infinity,
      props: oSchema.props
    };

    switch (oSchema.type) {
      case 'character':
        return new Character(baseConfig);
      case 'shape':
        return new Shape(baseConfig);
      case 'text':
        return new TextObject(baseConfig);
      case 'label':
        return new Label(baseConfig);
      case 'arrow':
        return new Arrow(baseConfig);
      case 'path':
        return new PathObject(baseConfig);
      case 'bubble':
        return new Bubble(baseConfig);
      case 'chalkboard':
        return new Chalkboard(baseConfig);
      case 'compass':
        return new Compass(baseConfig);
      case 'ruler':
        return new Ruler(baseConfig);
      case 'table':
        return new TableObject(baseConfig);
      case 'flowchart':
        return new FlowNode(baseConfig);
      case 'image':
        return new ImageObject(baseConfig);
      case 'group':
        return new GroupObject(baseConfig);
      case 'fire':
        return new FireEffect(baseConfig);
      case 'rain':
        return new RainEffect(baseConfig);
      case 'sparkles':
      case 'particles':
        return new SparklesEffect(baseConfig);
      case 'confetti':
        return new ConfettiEffect(baseConfig);
      case 'geometry':
        return new GeometryObject(baseConfig);
      case 'svg':
        return new RawSVGObject(baseConfig);
      case 'pointer':
        return new PointerObject(baseConfig);
      case 'spotlight':
        return new SpotlightObject(baseConfig);
      case 'subtitle':
        return new SubtitleOverlay(baseConfig);
      case 'screencast':
      case 'screen':
        return new ImageObject(baseConfig);
      default:
        return null;
    }
  }
}
